/**
 * Page de blocage — aucune organisation associée au compte.
 *
 * Affiché quand /auth/me retourne organization: null.
 * L'utilisateur est authentifié via Auth0 mais son compte n'est lié
 * à aucune organisation (company) sur la plateforme Wanzo.
 *
 * Navigation vers cette page :
 *   navigate('/no-organization', { replace: true })
 */

import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Building2, LogOut, ExternalLink, RefreshCw } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { setAuthTokenProvider } from '../../services/api/ApiService';
import { isAuditRole } from '../../utils/roles';

export function NoOrganizationPage() {
  const navigate = useNavigate();
  const { logout: auth0Logout, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { initialize, loading, logout: storeLogout } = useAuthStore();

  /**
   * Vérifie à nouveau si l'organisation est maintenant disponible.
   * Re-appelle /auth/me et redirige vers l'app si l'org est présente.
   */
  const handleRefresh = async () => {
    try {
      // S'assurer que le token provider est configuré
      if (isAuthenticated) {
        setAuthTokenProvider(async () => {
          try {
            return await getAccessTokenSilently();
          } catch {
            return null;
          }
        });
      }

      await initialize();

      const { organization, user: authUser } = useAuthStore.getState();

      // Auditeurs → page clients (ils n'ont pas d'organisation propre)
      if (isAuditRole(authUser?.role)) {
        navigate('/auditor/clients', { replace: true });
        return;
      }

      const hasOrganization = organization?.id || authUser?.organizationId;
      if (hasOrganization) {
        navigate('/', { replace: true });
      }
      // Si toujours pas d'org, on reste sur cette page
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'organisation:', error);
    }
  };

  /**
   * Déconnexion complète : nettoie le store local + invalide la session Auth0.
   */
  const handleLogout = async () => {
    await storeLogout();
    auth0Logout({ logoutParams: { returnTo: window.location.origin + '/login' } });
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 px-4">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md max-w-md w-full">
        <div className="text-center">

          {/* Icône */}
          <div className="mb-4">
            <Building2 className="mx-auto h-16 w-16 text-yellow-500 dark:text-yellow-400" />
          </div>

          {/* Titre */}
          <h1 className="text-2xl font-bold mb-4 text-yellow-600 dark:text-yellow-400">
            Aucune organisation associée
          </h1>

          {/* Message contextuel */}
          <div className="rounded-lg border bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700 p-4 mb-6 text-sm text-left">
            <p className="text-gray-700 dark:text-gray-300 mb-2">
              Votre compte est authentifié mais n'est lié à aucune organisation
              enregistrée sur la plateforme Wanzo Comptabilité.
            </p>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              Pour accéder à l'application, votre compte doit être rattaché à une
              organisation (entreprise). Créez ou rejoignez une organisation via
              le portail Wanzo.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col space-y-3">

            {/* Lien vers le portail Wanzo pour créer / rejoindre une organisation */}
            <a
              href="https://wanzzo.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              <ExternalLink className="h-4 w-4" />
              Créer ou associer une organisation sur Wanzo
            </a>

            {/* Vérifier à nouveau */}
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Vérifier à nouveau
            </button>

            {/* Déconnexion */}
            <button
              onClick={handleLogout}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm"
            >
              <LogOut className="h-4 w-4" />
              Se déconnecter
            </button>
          </div>

          <p className="mt-6 text-xs text-gray-400 dark:text-gray-500">
            Si vous pensez qu'il s'agit d'une erreur, contactez le support Wanzo.
          </p>
        </div>
      </div>
    </div>
  );
}

export default NoOrganizationPage;
